import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, AlertTriangle, Users, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import api from '@/lib/api';
import { getRiskColor, getRiskLevel, getRiskBadgeClass } from '@/lib/utils';
import { NavBar } from '@/components/NavBar';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const SectorDetailPage = () => {
  const { sectorName } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState(null);
  const [sectorNews, setSectorNews] = useState(null);

  useEffect(() => {
    fetchSectorData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorName]);

  const fetchSectorData = async () => {
    try {
      const response = await api.getSectorDetail(sectorName);
      setSector(response.data);
      // Load sector news non-blocking
      api.getSectorNews(sectorName).then(res => setSectorNews(res.data)).catch(() => {});
    } catch (error) {
      console.error('Error fetching sector data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-pulse text-[#4E6974]">Loading sector data...</div>
      </div>
    );
  }

  if (!sector) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#4E6974] mb-4">Sector not found</p>
          <Button onClick={() => navigate('/sectors')} variant="outline">
            Return to sectors
          </Button>
        </div>
      </div>
    );
  }

  const countryChartData = {
    labels: sector.country_risks?.map(c => c.country_name) || [],
    datasets: [{
      data: sector.country_risks?.map(c => c.sector_risk_score) || [],
      backgroundColor: sector.country_risks?.map(c => getRiskColor(c.sector_risk_score)) || [],
      borderRadius: 4,
      barThickness: 28,
    }]
  };

  const countryChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0A2533',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
      }
    },
    scales: {
      x: {
        max: 100,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { color: '#4E6974', font: { family: 'DM Sans' } }
      },
      y: {
        grid: { display: false },
        ticks: { color: '#0D3B4F', font: { family: 'DM Sans', weight: 500 } }
      }
    }
  };

  const TrendIcon = ({ trend }) => {
    switch (trend?.toLowerCase()) {
      case 'up': return <TrendingUp className="h-4 w-4 text-[#FF3B30]" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-[#2E7D32]" />;
      default: return <Minus className="h-4 w-4 text-[#4E6974]" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Navigation */}
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Back button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate('/sectors')}
            className="mt-6 text-[#4E6974] hover:text-[#0D3B4F]"
            data-testid="back-to-sectors-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to sectors
          </Button>

          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 mb-8"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-5xl">{sector.sector_icon}</span>
              <div>
                <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="sector-name">
                  {sector.sector_name}
                </h1>
                <p className="text-[#4E6974]">{sector.description}</p>
              </div>
              <div className="ml-auto flex items-center gap-4">
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <span 
                      className="text-4xl font-bold"
                      style={{ color: getRiskColor(sector.overall_risk_score) }}
                      data-testid="sector-risk-score"
                    >
                      {sector.overall_risk_score}
                    </span>
                    <span className="text-2xl text-[#4E6974]">/100</span>
                    <TrendIcon trend={sector.risk_trend} />
                  </div>
                  <Badge className={`${getRiskBadgeClass(getRiskLevel(sector.overall_risk_score))} mt-1`}>
                    {getRiskLevel(sector.overall_risk_score)} RISK
                  </Badge>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Country Risk Chart */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]">Risk by country</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <Bar data={countryChartData} options={countryChartOptions} />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Key Signals */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]">Key signals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {sector.key_signals?.map((signal, i) => (
                      <div 
                        key={i}
                        className="flex gap-3 p-4 rounded-lg bg-[#F5F5F5]"
                        data-testid={`sector-signal-${i}`}
                      >
                        <AlertTriangle className="h-5 w-5 text-[#FF9500] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-[#0D3B4F]">{signal}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Country Details */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.3 }}
            className="mt-6"
          >
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#0D3B4F]">Country breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {sector.country_risks?.map((country, i) => (
                    <div 
                      key={country.country_code}
                      className="p-4 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] transition-colors cursor-pointer"
                      onClick={() => navigate(`/countries/${country.country_code}`)}
                      data-testid={`country-breakdown-${i}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{country.flag_emoji}</span>
                        <span className="font-semibold text-[#0D3B4F]">{country.country_name}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span 
                          className="text-xl font-bold"
                          style={{ color: getRiskColor(country.sector_risk_score) }}
                        >
                          {country.sector_risk_score}
                        </span>
                        <span className="text-[#4E6974] text-sm">/100</span>
                      </div>
                      <p className="text-xs text-[#4E6974]">{country.key_factor}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* ILO Labour Cost Comparison + OEC Complexity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* ILO Wages */}
            {sector.ilo_wages?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="ilo-wages-section">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                        <Users className="h-5 w-5 text-[#0097A7]" /> Labour cost comparison
                      </CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">ILO</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-[#4E6974] mb-4">Manufacturing sector wages across monitored countries, sorted lowest to highest</p>
                    <div className="space-y-3">
                      {sector.ilo_wages.map((w) => {
                        const maxWage = Math.max(...sector.ilo_wages.map(x => x.manufacturing_wage_aud || 0), 1);
                        const pct = ((w.manufacturing_wage_aud || 0) / maxWage) * 100;
                        const safetyColor = w.safety_rating === 'poor' ? '#FF3B30' : w.safety_rating === 'excellent' ? '#2E7D32' : w.safety_rating === 'good' ? '#0097A7' : '#4E6974';
                        return (
                          <div key={w.country_code} className="flex items-center gap-3">
                            <span className="text-sm font-medium text-[#0D3B4F] w-24 truncate">{w.country_name}</span>
                            <div className="flex-1">
                              <div className="h-5 bg-[#F5F5F5] rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-[#0097A7]/70 transition-all flex items-center justify-end pr-2" style={{ width: `${Math.max(pct, 8)}%` }}>
                                  <span className="text-[10px] text-white font-bold">${w.manufacturing_wage_aud?.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 w-28">
                              <span className={`text-xs ${w.wage_growth_pct > 10 ? 'text-[#FF9500] font-medium' : 'text-[#4E6974]'}`}>+{w.wage_growth_pct}%</span>
                              <span className="text-[10px] capitalize" style={{ color: safetyColor }}>{w.safety_rating}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-4 italic">Source: International Labour Organisation (ILOSTAT). Wages in AUD/month.</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* OEC Economic Complexity */}
            {sector.oec_complexity?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="oec-complexity-section">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                        <Globe className="h-5 w-5 text-[#0097A7]" /> Economic complexity
                      </CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">OEC</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-[#4E6974] mb-4">Economic Complexity Index (ECI) measures a country's productive capabilities</p>
                    <div className="space-y-3">
                      {sector.oec_complexity.map((c) => (
                        <div key={c.country_code} className="flex items-center justify-between p-3 rounded-lg bg-[#F5F5F5] hover:bg-[#E0E0E0] transition-colors cursor-pointer" onClick={() => navigate(`/countries/${c.country_code}`)}>
                          <div>
                            <span className="text-sm font-medium text-[#0D3B4F]">{c.country_name}</span>
                            <span className="text-xs text-[#4E6974] ml-2 capitalize">{c.diversification}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-bold text-[#0D3B4F]">{c.eci?.toFixed(2)}</span>
                            <span className="text-xs text-[#4E6974] ml-1">#{c.eci_rank}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-4 italic">Source: Observatory of Economic Complexity. Higher ECI = more complex, diversified economy.</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Feature 4: Live Sector News */}
            {sectorNews?.articles?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-6">
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="sector-news-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F]">Sector news</CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">{sectorNews.source === 'gnews' ? 'GNews' : 'Intel'}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {sectorNews.articles.map((a, i) => (
                        <div key={a.title || i} className="p-3 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] transition-colors" data-testid={`sector-news-${i}`}>
                          <p className="text-sm font-medium text-[#0D3B4F]">{a.title}</p>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-[#4E6974]">
                            {a.source && <span>{a.source}</span>}
                            {a.date && <span>{a.date}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SectorDetailPage;
